"use client";

import Script from "next/script";
import { useEffect } from "react";

interface AdPixelsProps {
	metaPixelId?: string;
	googleAdsId?: string;
	linkedinPartnerId?: string;
}

/**
 * Ad Tracking Pixels
 * Loads Meta Pixel, Google Ads, LinkedIn Insight Tag
 * Only loads if the corresponding ID is provided
 */
export function AdPixels( { metaPixelId, googleAdsId, linkedinPartnerId }: AdPixelsProps ) {
	useEffect( () => {
		// Expose pixel IDs for tracking utilities
		if (typeof window !== "undefined") {
			const w = window as unknown as {
				__AD_PIXELS__: {
					meta?: string;
					google_ads?: string;
					linkedin?: string;
				};
			};
			w.__AD_PIXELS__ = {
				meta: metaPixelId,
				google_ads: googleAdsId,
				linkedin: linkedinPartnerId,
			};
		}
	}, [ metaPixelId, googleAdsId, linkedinPartnerId ] );

	const hasAnyPixel = metaPixelId || googleAdsId || linkedinPartnerId;
	if (!hasAnyPixel) return null;

	return (
		<>
			{/* Meta Pixel */}
			{metaPixelId && (
				<Script id="meta-pixel" strategy="afterInteractive">
					{`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${metaPixelId}');
            fbq('track', 'PageView');
          `}
				</Script>
			)}

			{/* Google Ads / Google Analytics (gtag) */}
			{googleAdsId && (
				<Script id="google-gtag" strategy="afterInteractive">
					{`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${googleAdsId}', {
              send_page_view: true
            });
          `}
				</Script>
			)}
			{googleAdsId && (
				<Script
					id="google-gtag-src"
					strategy="afterInteractive"
					src={`https://www.googletagmanager.com/gtag/js?id=${googleAdsId}`}
				/>
			)}

			{/* LinkedIn Insight Tag */}
			{linkedinPartnerId && (
				<Script id="linkedin-insight" strategy="afterInteractive">
					{`
            _linkedin_partner_id = "${linkedinPartnerId}";
            window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
            window._linkedin_data_partner_ids.push(_linkedin_partner_id);
            (function(l) {
              if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
              window.lintrk.q=[]}
              var s = document.getElementsByTagName("script")[0];
              var b = document.createElement("script");
              b.type = "text/javascript";b.async = true;
              b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
              s.parentNode.insertBefore(b, s);
            })(window.lintrk);
          `}
				</Script>
			)}

			{/* NoScript Fallbacks (for users with JS disabled) */}
			<noscript>
				{metaPixelId && (
					<img
						height="1"
						width="1"
						style={{ display: "none" }}
						alt=""
						src={`https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`}
					/>
				)}
			</noscript>
		</>
	);
}
